import { toast } from 'sonner';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { apiError, useImagingApi, useImagingCapabilities, useImagingQuery } from './api';
interface Profile {
  enabled_formulas: string[];
  registry: Record<string, { name: string; citation: string; release_status: string }>;
  revision: number;
}
export function CalculationProfile() {
  const cap = useImagingCapabilities();
  const allowed = cap.data?.permissions.some((p) => ['prepare_imaging', 'read_imaging'].includes(p));
  const query = useImagingQuery<Profile>('/profile', !!allowed);
  const { org, base } = useImagingApi();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[] | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const canManage = cap.data?.permissions.includes('manage_imaging_profile');
  async function save() {
    if (!query.data) return;
    setBusy(true);
    try {
      await clientApi.put(`${base}/profile`, {
        approval_note: note,
        revision: query.data.revision,
        enabled_formulas: selected ?? query.data.enabled_formulas,
      });
      await qc.invalidateQueries({ queryKey: ['imaging', org] });
      setSelected(null);
      setNote('');
      toast.success('Calculation profile saved. Affected drafts require review.');
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }
  if (!query.data) return null;
  return (
    <details className="rounded border p-4">
      <summary>Calculation profile · revision {query.data.revision}</summary>
      <div className="my-4 flex flex-col gap-4">
        {Object.entries(query.data.registry).map(([key, r]) => (
          <div key={key}>
            <label className="flex gap-2 items-center">
              <Checkbox
                disabled={!canManage || busy}
                checked={(selected ?? query.data!.enabled_formulas).includes(key)}
                onCheckedChange={(v) =>
                  setSelected(
                    v
                      ? [...(selected ?? query.data!.enabled_formulas), key]
                      : (selected ?? query.data!.enabled_formulas).filter((k) => k !== key),
                  )
                }
              />
              {r.name}
            </label>
            <p>{r.release_status}</p>
            <a target="_blank" rel="noreferrer" href={r.citation}>
              Reference source
            </a>
          </div>
        ))}
        {canManage && (
          <>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="imaging-approval">
                  Clinical validation and reference-use approval record
                </FieldLabel>
                <Textarea
                  id="imaging-approval"
                  value={note}
                  placeholder="Document the reviewed implementation, permitted reference use and approval record."
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
            </FieldGroup>
            <Button disabled={busy || note.trim().length < 10} onClick={() => void save()}>
              Save organization calculation profile
            </Button>
          </>
        )}
      </div>
    </details>
  );
}
